/* crm-integration.jsx — Phase 5: Webhooks, Import/Export, Public API, Plugins, Jobs, Search */
const { useState, useEffect, useMemo } = React;

const STATUS_PILL = (status, colors = {}) => {
  const c = { SUCCESS: '#16A34A', FAILED: '#EF4444', PENDING: '#F59E0B', RUNNING: '#3B82F6', ACTIVE: '#16A34A', INACTIVE: '#64748B', COMPLETED: '#16A34A', CANCELLED: '#EF4444', ...colors }[status] || '#64748B';
  return <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: c + '18', color: c }}>{status}</span>;
};

/* ── Enterprise Search ── */
const EnterpriseSearchView = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [entityTypes, setEntityTypes] = useState(['people', 'companies', 'deals']);
  const [loading, setLoading] = useState(false);
  const store = useStore?.();
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      let data = null;
      if (window.__nx_api?.crmSearch?.search) {
        data = await window.__nx_api.crmSearch.search(wsId, query, entityTypes).catch(() => null);
      }
      if (!data) {
        const q = query.toLowerCase();
        data = {
          people: entityTypes.includes('people') ? (store?.people || []).filter(p => (p.name || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q)).slice(0, 5) : [],
          companies: entityTypes.includes('companies') ? (store?.companies || []).filter(c => (c.name || '').toLowerCase().includes(q)).slice(0, 5) : [],
          deals: entityTypes.includes('deals') ? (store?.deals || []).filter(d => (d.title || '').toLowerCase().includes(q)).slice(0, 5) : [],
        };
      }
      setResults(data);
    } catch {}
    setLoading(false);
  };

  const totalResults = results ? Object.values(results).flat().length : 0;
  const ENTITY_ICONS = { people: 'User', companies: 'Building', deals: 'Briefcase', tasks: 'CheckSquare', notes: 'FileText' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--nx-border)' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--nx-text)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><NexusIcon name="Search" size={18} style={{ color: 'var(--nx-accent)' }} /> Enterprise Search</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()} placeholder="Search across people, companies, deals..." style={{ flex: 1, padding: '9px 14px', borderRadius: 8, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 14 }} />
          <button onClick={search} disabled={loading} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Search</button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {['people', 'companies', 'deals', 'tasks', 'notes'].map(t => (
            <button key={t} onClick={() => setEntityTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])} style={{ padding: '4px 12px', borderRadius: 20, border: `1px solid ${entityTypes.includes(t) ? 'var(--nx-accent)' : 'var(--nx-border)'}`, background: entityTypes.includes(t) ? 'var(--nx-accent-tint-2)' : 'transparent', color: entityTypes.includes(t) ? 'var(--nx-accent)' : 'var(--nx-text-muted)', fontSize: 12, fontWeight: entityTypes.includes(t) ? 700 : 400, cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {results === null && <div style={{ textAlign: 'center', padding: 48, color: 'var(--nx-text-muted)', fontSize: 13 }}>Enter a search query to find records across your CRM.</div>}
        {results !== null && totalResults === 0 && <div style={{ textAlign: 'center', padding: 48, color: 'var(--nx-text-muted)', fontSize: 13 }}>No results found for "{query}".</div>}
        {results !== null && Object.entries(results).map(([type, items]) => items.length === 0 ? null : (
          <div key={type} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <NexusIcon name={ENTITY_ICONS[type] || 'Circle'} size={12} /> {type.toUpperCase()} ({items.length})
            </div>
            {items.map((item, i) => (
              <div key={item.id || i} style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--nx-accent-tint-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <NexusIcon name={ENTITY_ICONS[type] || 'Circle'} size={13} style={{ color: 'var(--nx-accent)' }} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{item.name || item.title || item.email || item.id}</div>
                  {(item.email || item.value || item.phone) && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 1 }}>{item.email || (item.value ? `$${item.value.toLocaleString()}` : '') || item.phone}</div>}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Webhooks View ── */
const WebhooksView = () => {
  const [hooks, setHooks] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', url: '', events: [] });
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast?.() || {};

  const ALL_EVENTS = ['deal.created', 'deal.updated', 'deal.won', 'deal.lost', 'person.created', 'company.created', 'task.completed', 'meeting.scheduled', 'email.received', 'notification.created'];

  useEffect(() => {
    const load = async () => {
      if (!wsId || !window.__nx_api?.webhooks?.list) return;
      const d = await window.__nx_api.webhooks.list(wsId).catch(() => []);
      setHooks(Array.isArray(d) ? d : []);
    };
    load();
  }, [wsId]);

  useEffect(() => {
    const load = async () => {
      if (!selected || !wsId || !window.__nx_api?.webhooks?.listDeliveries) return;
      const d = await window.__nx_api.webhooks.listDeliveries(wsId, selected.id).catch(() => []);
      setDeliveries(Array.isArray(d) ? d : []);
    };
    load();
  }, [selected, wsId]);

  const handleSave = async () => {
    if (!form.name || !form.url || !wsId) return;
    try {
      const d = await window.__nx_api?.webhooks?.create(wsId, form);
      if (d) { setHooks(prev => [d, ...prev]); setAdding(false); setForm({ name: '', url: '', events: [] }); toast.show?.({ title: 'Webhook created', type: 'success' }); }
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!wsId) return;
    try { await window.__nx_api?.webhooks?.remove(wsId, id); setHooks(prev => prev.filter(h => h.id !== id)); if (selected?.id === id) setSelected(null); } catch {}
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--nx-text)', display: 'flex', alignItems: 'center', gap: 8 }}><NexusIcon name="Globe" size={18} style={{ color: 'var(--nx-accent)' }} /> Webhooks</div>
        <button onClick={() => setAdding(true)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ New Endpoint</button>
      </div>
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div style={{ width: 300, borderRight: '1px solid var(--nx-border)', overflowY: 'auto', padding: 12, flexShrink: 0 }}>
          {adding && (
            <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-accent)', borderRadius: 8, padding: 14, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Endpoint name" style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 12 }} />
              <input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://..." style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 12 }} />
              <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600 }}>EVENTS</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {ALL_EVENTS.map(ev => (
                  <button key={ev} onClick={() => setForm(f => ({ ...f, events: f.events.includes(ev) ? f.events.filter(e => e !== ev) : [...f.events, ev] }))} style={{ padding: '3px 8px', borderRadius: 20, border: `1px solid ${form.events.includes(ev) ? 'var(--nx-accent)' : 'var(--nx-border)'}`, background: form.events.includes(ev) ? 'var(--nx-accent-tint-2)' : 'transparent', color: form.events.includes(ev) ? 'var(--nx-accent)' : 'var(--nx-text-muted)', fontSize: 10, cursor: 'pointer' }}>{ev}</button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setAdding(false)} style={{ flex: 1, padding: '5px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'transparent', fontSize: 11, cursor: 'pointer', color: 'var(--nx-text-muted)' }}>Cancel</button>
                <button onClick={handleSave} style={{ flex: 1, padding: '5px', borderRadius: 6, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Save</button>
              </div>
            </div>
          )}
          {hooks.length === 0 && !adding && <div style={{ textAlign: 'center', padding: 24, color: 'var(--nx-text-muted)', fontSize: 12 }}>No webhooks configured.</div>}
          {hooks.map(h => (
            <div key={h.id} onClick={() => setSelected(h)} style={{ background: selected?.id === h.id ? 'var(--nx-accent-tint-2)' : 'var(--nx-surface)', border: `1px solid ${selected?.id === h.id ? 'var(--nx-accent)' : 'var(--nx-border)'}`, borderRadius: 8, padding: '10px 12px', marginBottom: 6, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{h.name}</span>
                <button onClick={e => { e.stopPropagation(); handleDelete(h.id); }} style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer' }}><NexusIcon name="Trash2" size={12} /></button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.url}</div>
              <div style={{ marginTop: 4 }}>{h.isActive ? STATUS_PILL('ACTIVE') : STATUS_PILL('INACTIVE')}</div>
            </div>
          ))}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {!selected ? (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--nx-text-muted)', fontSize: 13 }}>Select a webhook endpoint to view delivery history.</div>
          ) : (
            <>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--nx-text)', marginBottom: 4 }}>{selected.name}</div>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 16 }}>{selected.url}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                {(selected.events || []).map(ev => <span key={ev} style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', color: 'var(--nx-text-muted)' }}>{ev}</span>)}
              </div>
              {deliveries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: 'var(--nx-text-muted)', fontSize: 12 }}>No deliveries yet.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead><tr style={{ borderBottom: '1px solid var(--nx-border)' }}>{['Event', 'Status', 'Code', 'Duration', 'Time'].map(h => <th key={h} style={{ padding: '7px 10px', textAlign: 'left', color: 'var(--nx-text-muted)', fontWeight: 600 }}>{h}</th>)}</tr></thead>
                    <tbody>
                      {deliveries.map((d, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--nx-border)' }}>
                          <td style={{ padding: '7px 10px', color: 'var(--nx-text)' }}>{d.event}</td>
                          <td style={{ padding: '7px 10px' }}>{STATUS_PILL(d.status)}</td>
                          <td style={{ padding: '7px 10px', color: 'var(--nx-text-muted)' }}>{d.statusCode || '—'}</td>
                          <td style={{ padding: '7px 10px', color: 'var(--nx-text-muted)' }}>{d.durationMs ? `${d.durationMs}ms` : '—'}</td>
                          <td style={{ padding: '7px 10px', color: 'var(--nx-text-muted)' }}>{new Date(d.scheduledAt).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const _safeUrl = (url) => {
  if (!url) return null;
  try {
    const p = new URL(url);
    return ['http:', 'https:'].includes(p.protocol) ? url : null;
  } catch { return null; }
};

/* ── Import / Export View ── */
const ImportExportView = () => {
  const [tab, setTab] = useState('import');
  const [imports, setImports] = useState([]);
  const [exports, setExports] = useState([]);
  const [importForm, setImportForm] = useState({ entityType: 'people', fileName: '', totalRows: 0 });
  const [exportForm, setExportForm] = useState({ entityType: 'people', format: 'CSV' });
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast?.() || {};

  useEffect(() => {
    const load = async () => {
      if (!wsId) return;
      const [imp, exp] = await Promise.all([
        window.__nx_api?.importExport?.listImports?.(wsId).catch(() => []),
        window.__nx_api?.importExport?.listExports?.(wsId).catch(() => []),
      ]);
      setImports(Array.isArray(imp) ? imp : []);
      setExports(Array.isArray(exp) ? exp : []);
    };
    load();
  }, [wsId]);

  const handleImport = async () => {
    if (!importForm.fileName || !wsId) return;
    try {
      const d = await window.__nx_api?.importExport?.createImport(wsId, importForm);
      if (d) { setImports(prev => [d, ...prev]); toast.show?.({ title: 'Import job created', type: 'success' }); }
    } catch {}
  };

  const handleExport = async () => {
    if (!wsId) return;
    try {
      const d = await window.__nx_api?.importExport?.createExport(wsId, exportForm);
      if (d) { setExports(prev => [d, ...prev]); toast.show?.({ title: 'Export job created', type: 'success' }); }
    } catch {}
  };

  const ENTITIES = ['people', 'companies', 'deals', 'tasks', 'activities', 'notes'];
  const ProgressBar = ({ val, total }) => { const pct = total > 0 ? Math.round((val / total) * 100) : 0; return <div style={{ background: 'var(--nx-border)', borderRadius: 4, height: 6, marginTop: 4 }}><div style={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: 'var(--nx-accent)' }} /></div>; };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', gap: 0 }}>
        {['import', 'export'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '10px 20px', border: 'none', background: 'transparent', fontSize: 14, fontWeight: tab === t ? 700 : 400, color: tab === t ? 'var(--nx-accent)' : 'var(--nx-text-muted)', borderBottom: tab === t ? '2px solid var(--nx-accent)' : '2px solid transparent', cursor: 'pointer', textTransform: 'capitalize', display: 'flex', alignItems: 'center', gap: 6 }}>
            <NexusIcon name={t === 'import' ? 'Upload' : 'Download'} size={14} /> {t === 'import' ? 'Import' : 'Export'}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {tab === 'import' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: 18 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)', marginBottom: 14 }}>New Import</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600 }}>ENTITY TYPE</label>
                  <select value={importForm.entityType} onChange={e => setImportForm(f => ({ ...f, entityType: e.target.value }))} style={{ display: 'block', width: '100%', marginTop: 4, padding: '7px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 13 }}>
                    {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600 }}>FILE</label>
                  <input type="file" accept=".csv,.xlsx" onChange={e => setImportForm(f => ({ ...f, fileName: e.target.files?.[0]?.name || '' }))} style={{ display: 'block', width: '100%', marginTop: 4, padding: '5px 0', fontSize: 12, color: 'var(--nx-text)' }} />
                </div>
              </div>
              <button onClick={handleImport} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Start Import</button>
            </div>
            {imports.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--nx-text)', marginBottom: 10 }}>Recent Imports</div>
                {imports.map((imp, i) => (
                  <div key={imp.id || i} style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: '12px 14px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{imp.fileName || 'Import'}</span>
                      {STATUS_PILL(imp.status)}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 4 }}>{imp.entityType} · {imp.processedRows}/{imp.totalRows} rows · {imp.errorRows} errors</div>
                    <ProgressBar val={imp.processedRows} total={imp.totalRows} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {tab === 'export' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: 18 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)', marginBottom: 14 }}>New Export</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600 }}>ENTITY TYPE</label>
                  <select value={exportForm.entityType} onChange={e => setExportForm(f => ({ ...f, entityType: e.target.value }))} style={{ display: 'block', width: '100%', marginTop: 4, padding: '7px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 13 }}>
                    {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600 }}>FORMAT</label>
                  <select value={exportForm.format} onChange={e => setExportForm(f => ({ ...f, format: e.target.value }))} style={{ display: 'block', width: '100%', marginTop: 4, padding: '7px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 13 }}>
                    {['CSV', 'XLSX', 'JSON'].map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              </div>
              <button onClick={handleExport} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Start Export</button>
            </div>
            {exports.map((exp, i) => (
              <div key={exp.id || i} style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{exp.entityType} · {exp.format}</span>
                  {STATUS_PILL(exp.status)}
                </div>
                <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 4 }}>{exp.totalRows} rows · {new Date(exp.createdAt).toLocaleDateString()}</div>
                {(() => { const safe = _safeUrl(exp.fileUrl); return safe ? <a href={safe} download style={{ fontSize: 12, color: 'var(--nx-accent)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}><NexusIcon name="Download" size={12} /> Download</a> : null; })()}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Plugins View ── */
const PluginsView = () => {
  const [plugins, setPlugins] = useState([]);
  const [tab, setTab] = useState('installed');
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast?.() || {};

  const MARKETPLACE = [
    { slug: 'slack-notifications', name: 'Slack Notifications', version: '2.1.0', author: 'URSA', description: 'Post CRM events to Slack channels', icon: 'MessageSquare', permissions: ['notifications.read', 'webhook.create'] },
    { slug: 'zapier-bridge', name: 'Zapier Bridge', version: '1.4.2', author: 'URSA', description: 'Connect CRM to 5000+ apps via Zapier', icon: 'Zap', permissions: ['webhook.create'] },
    { slug: 'docusign', name: 'DocuSign', version: '1.0.5', author: 'URSA', description: 'Send quotes and contracts for e-signature', icon: 'FileText', permissions: ['quotes.read', 'documents.create'] },
    { slug: 'calendly', name: 'Calendly', version: '1.2.0', author: 'URSA', description: 'Sync meetings with Calendly scheduling links', icon: 'Calendar', permissions: ['meetings.create'] },
  ];

  useEffect(() => {
    const load = async () => {
      if (!wsId || !window.__nx_api?.plugins?.list) return;
      const d = await window.__nx_api.plugins.list(wsId).catch(() => []);
      setPlugins(Array.isArray(d) ? d : []);
    };
    load();
  }, [wsId]);

  const handleInstall = async (pkg) => {
    if (!wsId) return;
    try {
      const d = await window.__nx_api?.plugins?.install(wsId, { name: pkg.name, slug: pkg.slug, version: pkg.version, description: pkg.description, permissions: pkg.permissions });
      if (d) { setPlugins(prev => [d, ...prev]); toast.show?.({ title: `${pkg.name} installed`, type: 'success' }); }
    } catch {}
  };

  const handleUninstall = async (id) => {
    if (!wsId) return;
    try { await window.__nx_api?.plugins?.uninstall(wsId, id); setPlugins(prev => prev.filter(p => p.id !== id)); } catch {}
  };

  const handleToggle = async (id, isActive) => {
    if (!wsId) return;
    try {
      const d = await window.__nx_api?.plugins?.toggle(wsId, id, isActive);
      if (d) setPlugins(prev => prev.map(p => p.id === id ? d : p));
    } catch {}
  };

  const installedSlugs = new Set(plugins.map(p => p.slug));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', gap: 0 }}>
        {['installed', 'marketplace'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '10px 20px', border: 'none', background: 'transparent', fontSize: 14, fontWeight: tab === t ? 700 : 400, color: tab === t ? 'var(--nx-accent)' : 'var(--nx-text-muted)', borderBottom: tab === t ? '2px solid var(--nx-accent)' : '2px solid transparent', cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {tab === 'installed' && (plugins.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--nx-text-muted)' }}>
            <NexusIcon name="Cpu" size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
            <div style={{ fontSize: 15, fontWeight: 600 }}>No plugins installed</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Browse the marketplace to extend CRM functionality.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {plugins.map(p => (
              <div key={p.id} style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--nx-accent-tint-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <NexusIcon name="Cpu" size={18} style={{ color: 'var(--nx-accent)' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>{p.name}</span>
                    <span style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>v{p.version}</span>
                    {p.isActive ? STATUS_PILL('ACTIVE') : STATUS_PILL('INACTIVE')}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>{p.description}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => handleToggle(p.id, !p.isActive)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'transparent', fontSize: 11, color: 'var(--nx-text-muted)', cursor: 'pointer' }}>{p.isActive ? 'Disable' : 'Enable'}</button>
                  <button onClick={() => handleUninstall(p.id)} style={{ padding: '4px 8px', borderRadius: 6, border: 'none', background: '#EF444418', color: '#EF4444', fontSize: 11, cursor: 'pointer' }}>Uninstall</button>
                </div>
              </div>
            ))}
          </div>
        ))}
        {tab === 'marketplace' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {MARKETPLACE.map(pkg => (
              <div key={pkg.slug} style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 12, padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--nx-accent-tint-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <NexusIcon name={pkg.icon} size={18} style={{ color: 'var(--nx-accent)' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>{pkg.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>v{pkg.version} · {pkg.author}</div>
                  </div>
                </div>
                <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 12, lineHeight: 1.4 }}>{pkg.description}</div>
                <button onClick={() => handleInstall(pkg)} disabled={installedSlugs.has(pkg.slug)} style={{ width: '100%', padding: '8px', borderRadius: 8, border: `1px solid ${installedSlugs.has(pkg.slug) ? 'var(--nx-border)' : 'var(--nx-accent)'}`, background: installedSlugs.has(pkg.slug) ? 'transparent' : 'var(--nx-accent-tint-2)', color: installedSlugs.has(pkg.slug) ? 'var(--nx-text-muted)' : 'var(--nx-accent)', fontSize: 13, fontWeight: 600, cursor: installedSlugs.has(pkg.slug) ? 'default' : 'pointer' }}>
                  {installedSlugs.has(pkg.slug) ? 'Installed' : 'Install'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Duplicate Detection View ── */
const DuplicateDetectionView = () => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const [entityType, setEntityType] = useState('people');
  const [results, setResults] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [merging, setMerging] = useState(null);
  const store = useStore?.();
  const toast = useToast?.() || {};

  const _similarity = (a, b) => {
    if (!a || !b) return 0;
    if (a === b) return 100;
    const longer = a.length > b.length ? a : b;
    const shorter = a.length > b.length ? b : a;
    let matches = 0;
    for (let i = 0; i < shorter.length; i++) { if (longer.includes(shorter[i])) matches++; }
    return Math.round((matches / longer.length) * 100);
  };

  const scan = async () => {
    setScanning(true);
    await new Promise(r => setTimeout(r, 800));
    const items = entityType === 'people' ? (store?.people || []) : (store?.companies || []);
    const dupes = [];
    const seen = {};
    items.forEach(item => {
      const key = (item.email || item.name || '').toLowerCase().trim();
      if (!key) return;
      if (seen[key]) {
        const prev = seen[key];
        const emailSim = item.email && prev.email ? _similarity(item.email.toLowerCase(), prev.email.toLowerCase()) : 0;
        const nameSim = item.name && prev.name ? _similarity(item.name.toLowerCase(), prev.name.toLowerCase()) : 0;
        const confidence = Math.max(emailSim, nameSim);
        dupes.push({ a: prev, b: item, confidence, matchOn: item.email ? 'email' : 'name' });
      } else seen[key] = item;
    });
    setResults(dupes);
    setScanning(false);
    if (dupes.length === 0) toast.show?.({ title: 'No duplicates found', type: 'success' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--nx-border)' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--nx-text)', display: 'flex', alignItems: 'center', gap: 8 }}><NexusIcon name="GitMerge" size={18} style={{ color: 'var(--nx-accent)' }} /> Duplicate Detection & Merge</div>
        <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center' }}>
          <select value={entityType} onChange={e => setEntityType(e.target.value)} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 13 }}>
            <option value="people">Contacts</option>
            <option value="companies">Companies</option>
          </select>
          <button onClick={scan} disabled={scanning} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {scanning ? 'Scanning...' : 'Scan for Duplicates'}
          </button>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {results.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--nx-text-muted)', fontSize: 13 }}>Run a scan to detect duplicate records in your CRM.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>{results.length} potential duplicate pair{results.length !== 1 ? 's' : ''} found</div>
            {results.map((dup, i) => (
              <div key={i} style={{ background: 'var(--nx-surface)', border: '1px solid #F59E0B', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#F59E0B18', color: '#F59E0B' }}>{dup.confidence}% match</span>
                  <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Matched on: {dup.matchOn}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 10, alignItems: 'center' }}>
                  <div style={{ background: 'var(--nx-bg)', borderRadius: 7, padding: '8px 12px', fontSize: 13 }}>
                    <div style={{ fontWeight: 700, color: 'var(--nx-text)' }}>{dup.a.name || dup.a.email}</div>
                    {dup.a.email && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{dup.a.email}</div>}
                  </div>
                  <NexusIcon name="ArrowRight" size={16} style={{ color: 'var(--nx-text-muted)' }} />
                  <div style={{ background: 'var(--nx-bg)', borderRadius: 7, padding: '8px 12px', fontSize: 13 }}>
                    <div style={{ fontWeight: 700, color: 'var(--nx-text)' }}>{dup.b.name || dup.b.email}</div>
                    {dup.b.email && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{dup.b.email}</div>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                  <button onClick={() => setResults(r => r.filter((_, idx) => idx !== i))} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'transparent', fontSize: 12, color: 'var(--nx-text-muted)', cursor: 'pointer' }}>Dismiss</button>
                  <button
                    disabled={merging === i}
                    onClick={async () => {
                      if (!wsId || merging !== null) return;
                      setMerging(i);
                      try {
                        await window.__nx_api?.importExport?.mergeDuplicates(wsId, { entityType, masterId: dup.a.id, duplicateId: dup.b.id });
                        toast.show?.({ title: 'Records merged successfully', type: 'success' });
                        setResults(r => r.filter((_, idx) => idx !== i));
                      } catch (e) {
                        toast.show?.({ title: e?.message || 'Merge failed', type: 'error' });
                      }
                      setMerging(null);
                    }}
                    style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: merging === i ? '#6366f180' : 'var(--nx-accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: merging === i ? 'default' : 'pointer' }}
                  >{merging === i ? 'Merging...' : 'Merge'}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Data Quality View ── */
const DataQualityView = () => {
  const store = useStore?.();
  const [entityType, setEntityType] = useState('people');

  const stats = useMemo(() => {
    const items = entityType === 'people' ? (store?.people || []) : entityType === 'companies' ? (store?.companies || []) : (store?.deals || []);
    if (!items.length) return { total: 0, completeness: 0, fields: [] };
    const checks = entityType === 'people'
      ? [['email', i => !!i.email], ['phone', i => !!i.phone], ['company', i => !!i.companyId], ['title', i => !!i.title], ['owner', i => !!i.ownerId]]
      : entityType === 'companies'
      ? [['website', i => !!i.website], ['phone', i => !!i.phone], ['industry', i => !!i.industry], ['address', i => !!i.address], ['owner', i => !!i.ownerId]]
      : [['close date', i => !!i.expectedCloseDate], ['value', i => !!i.value && i.value > 0], ['owner', i => !!i.ownerId], ['person', i => !!i.personId], ['company', i => !!i.companyId]];
    const fields = checks.map(([label, fn]) => { const filled = items.filter(fn).length; return { label, filled, total: items.length, pct: Math.round((filled / items.length) * 100) }; });
    const completeness = Math.round(fields.reduce((s, f) => s + f.pct, 0) / fields.length);
    return { total: items.length, completeness, fields };
  }, [store, entityType]);

  const grade = stats.completeness >= 80 ? { label: 'Good', color: '#16A34A' } : stats.completeness >= 60 ? { label: 'Fair', color: '#F59E0B' } : { label: 'Poor', color: '#EF4444' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--nx-text)', display: 'flex', alignItems: 'center', gap: 8 }}><NexusIcon name="CheckCircle" size={18} style={{ color: 'var(--nx-accent)' }} /> Data Quality Engine</div>
        <select value={entityType} onChange={e => setEntityType(e.target.value)} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 13 }}>
          {['people', 'companies', 'deals'].map(e => <option key={e} value={e}>{e}</option>)}
        </select>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 20, marginBottom: 24 }}>
          <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 12, padding: '24px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, fontWeight: 800, color: grade.color }}>{stats.completeness}%</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: grade.color, marginBottom: 4 }}>{grade.label}</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{stats.total} records</div>
          </div>
          <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--nx-text)', marginBottom: 14 }}>Field Completeness</div>
            {stats.fields.map((f, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: 'var(--nx-text)', fontWeight: 600, textTransform: 'capitalize' }}>{f.label}</span>
                  <span style={{ color: 'var(--nx-text-muted)' }}>{f.filled}/{f.total} ({f.pct}%)</span>
                </div>
                <div style={{ background: 'var(--nx-border)', borderRadius: 4, height: 8 }}>
                  <div style={{ width: `${f.pct}%`, height: '100%', borderRadius: 4, background: f.pct >= 80 ? '#16A34A' : f.pct >= 50 ? '#F59E0B' : '#EF4444', transition: 'width 0.5s' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Integration Hub View ── */
const IntegrationHubView = () => {
  const [tab, setTab] = useState('search');
  const TABS = [
    { id: 'search', label: 'Enterprise Search', icon: 'Search' },
    { id: 'webhooks', label: 'Webhooks', icon: 'Globe' },
    { id: 'import-export', label: 'Import / Export', icon: 'Upload' },
    { id: 'duplicates', label: 'Duplicates', icon: 'GitMerge' },
    { id: 'quality', label: 'Data Quality', icon: 'CheckCircle' },
    { id: 'plugins', label: 'Plugins', icon: 'Cpu' },
  ];
  return (
    <div style={{ display: 'flex', height: '100%' }}>
      <div style={{ width: 200, borderRight: '1px solid var(--nx-border)', padding: '16px 0', flexShrink: 0 }}>
        <div style={{ padding: '0 16px 12px', fontSize: 12, fontWeight: 700, color: 'var(--nx-text-muted)' }}>INTEGRATION HUB</div>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ width: '100%', padding: '9px 16px', border: 'none', background: tab === t.id ? 'var(--nx-accent-tint-2)' : 'transparent', color: tab === t.id ? 'var(--nx-accent)' : 'var(--nx-text-muted)', fontSize: 13, fontWeight: tab === t.id ? 700 : 400, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, borderLeft: tab === t.id ? '3px solid var(--nx-accent)' : '3px solid transparent' }}>
            <NexusIcon name={t.icon} size={14} /> {t.label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {tab === 'search' && <EnterpriseSearchView />}
        {tab === 'webhooks' && <WebhooksView />}
        {tab === 'import-export' && <ImportExportView />}
        {tab === 'duplicates' && <DuplicateDetectionView />}
        {tab === 'quality' && <DataQualityView />}
        {tab === 'plugins' && <PluginsView />}
      </div>
    </div>
  );
};

window.IntegrationHubView = IntegrationHubView;
window.EnterpriseSearchView = EnterpriseSearchView;
window.DuplicateDetectionView = DuplicateDetectionView;
window.DataQualityView = DataQualityView;
window.WebhooksView = WebhooksView;
window.ImportExportView = ImportExportView;
window.PluginsView = PluginsView;
